/**
 * Resolves the pdf.js worker in a way that survives badly configured hosts.
 *
 * pdf.js imports the worker as an ES module, and browsers enforce the served
 * MIME type: our self-hosted nginx serves `/assets/*.mjs` as
 * `application/octet-stream`, so the import is refused and the whole reader
 * fails ("Setting up fake worker failed"). Fetching the bytes ourselves and
 * running them from a Blob whose type we set removes that dependency.
 */
import * as pdfjs from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { createLogger } from "@/lib/frontend-logger";

const logger = createLogger("session.pdfWorker");

export const pdfWorkerUrl = workerUrl;

/** What the host actually returned for the worker asset, for failure reports. */
export type PdfWorkerInfo = {
  /** How the worker ended up being loaded. */
  mode: "blob" | "url";
  status?: number;
  contentType?: string | null;
};

let pending: Promise<PdfWorkerInfo> | null = null;

/**
 * Points `GlobalWorkerOptions.workerSrc` at a Blob copy of the worker script.
 * Resolves once per page load; on any failure it falls back to the plain URL so
 * correctly configured hosts behave exactly as before.
 */
export function ensurePdfWorker(): Promise<PdfWorkerInfo> {
  if (pending) return pending;
  pending = (async (): Promise<PdfWorkerInfo> => {
    try {
      const res = await fetch(workerUrl, { cache: "force-cache" });
      const contentType = res.headers.get("content-type");
      if (!res.ok) {
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
        logger.warn("pdf worker asset not ok", { status: res.status, contentType, workerUrl });
        return { mode: "url", status: res.status, contentType };
      }
      const code = await res.text();
      const blobUrl = URL.createObjectURL(new Blob([code], { type: "text/javascript" }));
      pdfjs.GlobalWorkerOptions.workerSrc = blobUrl;
      logger.info("pdf worker ready", { mode: "blob", contentType, bytes: code.length });
      return { mode: "blob", status: res.status, contentType };
    } catch (e) {
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
      logger.warn("pdf worker fetch failed, using direct url", {
        detail: String(e),
        workerUrl,
      });
      return { mode: "url" };
    }
  })();
  return pending;
}
